import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { isAppKey, type AppKey } from '@qms360/shared';
import { fetchAppSettings } from '../lib/api.js';
import { AppTopBar, Breadcrumbs } from '../components/AppTopBar.js';
import { PageMessage } from './AppHome.js';
import { t } from '../lib/i18n.js';

/**
 * An application's OWN Settings (ADR-0001).
 *
 * Settings is not one shared screen. Escalation rules and AI settings are
 * configured per application, from that application's own Settings, without
 * affecting the other two (spec §12, §13.3). The shared admin services underneath
 * are built once; the surfaces are per app.
 */

/**
 * Where a settings tile goes, for the ones that have a screen.
 *
 * Both of these land on the Master Data & Configuration Center: the escalation
 * matrix IS master data (spec §12's numbers are rule rows), and separating them
 * into two screens would mean editing an SLA in one place and the target it
 * compares against in another.
 *
 * A tile with no route renders as plain text, the same rule AppHome follows — a
 * link that goes nowhere teaches people the navigation is unreliable.
 */
const SECTION_ROUTE: Record<string, (app: string) => string> = {
  'master-data': (app) => `/${app}/settings/master-data`,
  'escalation-matrix': (app) => `/${app}/settings/master-data`,
  rbac: (app) => `/${app}/settings/access`,
  'audit-log': (app) => `/${app}/settings/audit-log`,
  'ai-settings': (app) => `/${app}/settings/ai`,
  integrations: (app) => `/${app}/settings/integrations`,
  'data-sync': (app) => `/${app}/settings/data-sync`,
  distribution: (app) => `/${app}/settings/distribution`,
};
export function AppSettings() {
  const params = useParams<{ app: string }>();
  const app = params.app;

  const { data, isLoading } = useQuery({
    queryKey: ['app-settings', app],
    queryFn: () => fetchAppSettings(app!),
    enabled: Boolean(app) && isAppKey(app),
  });

  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings']} />

      <main className="mx-auto max-w-7xl px-4 pb-8 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Settings")}</h1>
        <p className="mt-2 text-content-secondary">
          {t("These settings apply to this application only. Changing them does not affect the other two.")}
        </p>

        {isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}

        {data && data.sections.length === 0 && (
          <section
            className="mt-6 rounded-md border border-border bg-tone-info-surface p-6"
            role="status"
          >
            <h2 className="m-0 text-base font-semibold">{t("Nothing to configure")}</h2>
            <p className="mb-0 mt-2 text-content-secondary">
              {t("Your role does not include configuration authority in this application.")}
            </p>
          </section>
        )}

        {data && data.sections.length > 0 && (
          <ul className="mt-6 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2">
            {data.sections.map((section) => (
              <li
                key={section.key}
                className="rounded-md border border-border bg-surface p-5 shadow-card"
              >
                <h2 className="m-0 text-base font-semibold">
                  {SECTION_ROUTE[section.key] ? (
                    <Link
                      to={SECTION_ROUTE[section.key]!(app)}
                      className="text-ag-purple no-underline hover:underline"
                    >
                      {t(section.label)}
                    </Link>
                  ) : (
                    section.label
                  )}
                </h2>
                <p className="mb-0 mt-2 text-sm text-content-secondary">{section.description}</p>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
